import React, {useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import FormFooter from '../common/FormFooter';
import Spinner from '../common/Spinner';
import Select from '../common/Select';
import Input from '../common/Input';
import IdExamples from '../form/IdExamples';

import {postIdData} from '../../api/api';
import useScrollTop from '../../hooks/useScrollTop';
import {formSteps, validations} from '../../constants/form';
import {doValidation} from '../../helpers/form-validation';
import {useDataProviderState, useDataProviderActions} from '../../providers/DataProvider';
import {theme} from "../../App.jsx";
import Link from "@mui/material/Link";
import ClearingBanner from '../form/ClearingBanner';
import HighlightBox from "../common/HighlightBox.jsx";
import clearingImg from "../../assets/img/clearing.png";
import {TrackGoogleAnalyticsEvent} from "../../google-analytics/gaTracker.js";

export default function IdDataStep() {

    const {uid, data, initialData, loading, userData} = useDataProviderState();
    const {nextStep, saveData, setLoading, setErrorMessage} = useDataProviderActions();

    const [formData, setFormData] = useState(data[formSteps.ID_DATA]);
    const [errors, setErrors] = useState({});
    const [selectedIdType, setSelectedIdType] = useState(0);

    const formValidations = validations[formSteps.ID_DATA];

    useScrollTop();

    TrackGoogleAnalyticsEvent("IdDataStep");

    function handleFieldChange(event) {
        let {value, name, type} = event.target;

        if (formValidations[name].regexReplace) {
            if (formValidations[name].regexReplace.test(value)) {
                setFormData({...formData, [name]: value});
            }
        } else {
            setFormData({...formData, [name]: value});
        }

        if (errors[name] || type === 'select-one') {
            updateErrors(event, name);
        }
    }

    function handleFieldBlur(event) {
        let {name} = event.target;
        updateErrors(event, name);
    }

    function handleCheckboxChange(event) {
        const {checked, name} = event.target;

        setFormData({...formData, [name]: checked});
        updateErrors(event, name);
    }

    function updateErrors(event, name) {
        setErrors({
            ...errors,
            [name]: doValidation(event, formValidations[name]),
        });
    }

    function isPartnerVisible() {
        return ['2', '6'].includes(formData.estadoCivil);
    }

    function isFormValid() {
        const updatedErrors = {
            estadoCivil: doValidation({target: {value: formData.estadoCivil}}, formValidations.estadoCivil),
            ciConcubino: isPartnerVisible() ? doValidation({target: {value: formData.ciConcubino}}, formValidations.ciConcubino) : '',
            nombreConcubino: isPartnerVisible() ? doValidation({target: {value: formData.nombreConcubino}}, formValidations.nombreConcubino) : '',
            apellidoConcubino: isPartnerVisible() ? doValidation({target: {value: formData.apellidoConcubino}}, formValidations.apellidoConcubino) : '',
            nivelEstudio: doValidation({target: {value: formData.nivelEstudio}}, formValidations.nivelEstudio),
            nacionalidad: doValidation({target: {value: formData.nacionalidad}}, formValidations.nacionalidad),
            nroSerie: doValidation({target: {value: formData.nroSerie}}, formValidations.nroSerie),
            nroIdentificacion: (selectedIdType === 1) ? doValidation({target: {value: formData.nroIdentificacion}}, formValidations.nroIdentificacion) : '',
            //inListaPep: doValidation({target: {value: formData.inListaPep}}, formValidations.inListaPep),
            agreeBeneficiarioFinal: doValidation({
                target: {
                    checked: formData.agreeBeneficiarioFinal,
                    type: 'checkbox'
                }
            }, formValidations.agreeBeneficiarioFinal),
        };

        setErrors(updatedErrors);

        return !Object.values(updatedErrors).some(Boolean);
    }

    async function handleNext() {
        if (isFormValid()) {
            setLoading(true);
            setErrors({});
            const response = await postIdData(formData, uid);

            if (response?.success) {
                saveData(formData, formSteps.ID_DATA);
                nextStep();
            } else if (response?.errors) {
                setErrors(response.errors);
            } else {
                setErrorMessage();
            }

            setLoading(false);
        }
    }

    return (
        <Container component="section" maxWidth="sm">

            {/*<ClearingBanner/>*/}
            <Box sx={{pb: 2}}>
                <HighlightBox>
                    <Box sx={{display: 'flex'}}>
                        <Box sx={{py: 2}}>
                            <Box className="color-primary" sx={{pl: 2, pb: 1, fontSize: '1.3rem'}}>
                                <b>{userData.nombre}, continúa avanzando con tranquilidad</b>
                            </Box>
                            <Box as="span" sx={{
                                display: 'inline-block',
                                px: 2,
                                color: 'white',
                                background: 'var(--color-primary)',
                                borderBottomRightRadius: '15px',
                                borderTopRightRadius: '15px',
                                fontSize: '1.2rem'
                            }}>
                                Cuidamos el valor de tus datos
                            </Box>
                            <Box sx={{p: 1, pl: 2}}>
                                <b className="msg">Con tu C.I. consultaremos tu puntaje crediticio, si el resultado es bueno, en unos minutos obtendrás un préstamo a tu medida.</b>
                            </Box>
                        </Box>
                        <Box sx={{display: {xs: 'none', sm: 'block'}}}>
                            <img src={clearingImg} width="189" height="180" style={{padding: '5px'}}/>
                        </Box>
                    </Box>
                </HighlightBox>
            </Box>

            <IdExamples
                selectedIdType={selectedIdType}
                onIdTypeChange={setSelectedIdType}
            />

            <Select
                sx={{pb: 1}}
                label="Estado civil"
                name="estadoCivil"
                formData={formData}
                onChange={handleFieldChange}
                errors={errors}
                formValidations={formValidations}
                initialData={initialData}
                handleFieldChange={handleFieldChange}
            />

            {isPartnerVisible() ? (<Input
                sx={{pb: 1}}
                label="CI de cónyuge o concubino/a"
                name="ciConcubino"
                placeholder="Ej: 41237895"
                formData={formData}
                errors={errors}
                formValidations={formValidations}
                handleFieldChange={handleFieldChange}
                handleFieldBlur={handleFieldBlur}
            />) : null}

            {isPartnerVisible() ? (<Input
                sx={{pb: 1}}
                label="Nombre cónyuge o concubino/a"
                name="nombreConcubino"
                formData={formData}
                errors={errors}
                formValidations={formValidations}
                handleFieldChange={handleFieldChange}
                handleFieldBlur={handleFieldBlur}
            />) : null}

            {isPartnerVisible() ? (<Input
                sx={{pb: 1}}
                label="Apellido cónyuge o concubino/a"
                name="apellidoConcubino"
                formData={formData}
                errors={errors}
                formValidations={formValidations}
                handleFieldChange={handleFieldChange}
                handleFieldBlur={handleFieldBlur}
            />) : null}

            <Select
                sx={{pb: 1}}
                label="Nacionalidad"
                name="nacionalidad"
                formData={formData}
                onChange={handleFieldChange}
                errors={errors}
                formValidations={formValidations}
                initialData={initialData}
                handleFieldChange={handleFieldChange}
            />

            <Select
                sx={{pb: 1}}
                label="Nivel de estudio"
                name="nivelEstudio"
                formData={formData}
                onChange={handleFieldChange}
                errors={errors}
                formValidations={formValidations}
                initialData={initialData}
                handleFieldChange={handleFieldChange}
            />

            <Grid container spacing={{xs: 0, sm: 2}}>
                <Grid item sm={(selectedIdType === 1) ? 6 : 12} xs={12}>
                    <Input
                        sx={{pb: 1}}
                        label="Serie"
                        name="nroSerie"
                        formData={formData}
                        errors={errors}
                        formValidations={formValidations}
                        handleFieldChange={handleFieldChange}
                        handleFieldBlur={handleFieldBlur}
                    />
                </Grid>

                {(selectedIdType === 1) ? (<Grid item sm={6} xs={12}>
                    <Input
                        sx={{pb: 1}}
                        label="Identificación"
                        name="nroIdentificacion"
                        formData={formData}
                        errors={errors}
                        formValidations={formValidations}
                        handleFieldChange={handleFieldChange}
                        handleFieldBlur={handleFieldBlur}
                    />
                </Grid>) : null}
            </Grid>

            {/*<FormControl sx={{ pt: 2 }}>
                <FormLabel id="inListaPep-label">No soy una Persona Políticamente Expuesta (PEP)</FormLabel>
                <RadioGroup
                    row
                    aria-labelledby="inListaPep-label"
                    name="inListaPep"
                    value={formData.inListaPep}
                    onChange={handleFieldChange}
                    required>
                    <FormControlLabel value={true} control={<Radio />} label="Si" />
                    <FormControlLabel value={false} control={<Radio />} label="No" />
                </RadioGroup>
            </FormControl>
            <FormHelperText error={!!errors.inListaPep}>{errors.inListaPep}</FormHelperText>*/}

            <FormControlLabel
                sx={{pt: 1, pl: 1}}
                control={
                    <Checkbox
                        checked={formData.agreeBeneficiarioFinal}
                        onChange={handleCheckboxChange}
                        name="agreeBeneficiarioFinal"
                        color="primary"
                        sx={{
                            '& .MuiSvgIcon-root': {
                                color: theme.palette.primary.main
                            }
                        }}
                    />
                }
                label="Declaro ser beneficiario/a final del préstamo. *"
            />
            <FormHelperText sx={{
                mt: 0,
                pt: 0,
                pl: 1
            }} error={!!errors.agreeBeneficiarioFinal}>{errors.agreeBeneficiarioFinal}</FormHelperText>

            <FormFooter onNext={handleNext}/>

            <Spinner loading={loading}/>
        </Container>
    )

}
